import { localUrl as appLocalUrl, assetUrl as appAssetUrl } from '../app-path.js';
import SessionManager from '../session-manager';
import FavoriteCityService from './favorite-city-service';
import SubscriptionSyncService from '../services/subscription-sync-service';
import ApiClient from '../api-client';
import { buildFavoriteCityForecastUrl } from './favorite-cities-navigation';
import LocalStorageHelper from './local-storage-helper';

const CONTAINER_ID = 'homepageFavoriteCities';
const LOAD_TIMEOUT_MS = 5000;

let favorites = [];
let expandedCardId = null;
let isInternalUpdate = false;
let citySelectionHandler = null;

const isPremiumUser = () => {
	try {
		const raw = localStorage.getItem('ct_mc');
		if (!raw) return false;
		const parsed = JSON.parse(raw);
		return parsed.mcsp && parsed.mcsp !== 'free';
	} catch {
		return false;
	}
};

const removeContainer = () => {
	const container = document.getElementById(CONTAINER_ID);
	if (container) {
		container.remove();
	}
};

const syncSubscriptionPlan = async () => {
	if (!SessionManager.isAuthenticated() || isPremiumUser()) {
		return;
	}

	try {
		await SubscriptionSyncService.syncPlan(ApiClient, true);
	} catch (error) {
		console.error('Erro ao atualizar plano antes de carregar favoritos:', error);
	}
};

const loadFavorites = async () => {
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), LOAD_TIMEOUT_MS);

	try {
		const result = await Promise.race([
			FavoriteCityService.refreshServerFavorites(true).then(() =>
				FavoriteCityService.getVisibleFavorites(),
			),
			new Promise((_, reject) => {
				controller.signal.addEventListener('abort', () =>
					reject(new Error('timeout')),
				);
			}),
		]);

		clearTimeout(timeoutId);
		return result;
	} catch {
		clearTimeout(timeoutId);
		try {
			return FavoriteCityService.getVisibleFavorites();
		} catch {
			return null;
		}
	}
};

const handleDelete = async (cityId, cardElement) => {
	try {
		isInternalUpdate = true;
		const favorite = favorites.find(
			item => Number(item?.city?.cid) === Number(cityId),
		);
		let result;
		try {
			result = await FavoriteCityService.removeFavorite(
				favorite?.city || { cid: cityId, cloc: cityId },
			);
		} finally {
			isInternalUpdate = false;
		}
		if (result.status === 'error') {
			throw result.error || new Error('Erro ao excluir cidade');
		}
		favorites = FavoriteCityService.getVisibleFavorites();
		renderSection(favorites);
	} catch {
		const errorMsg = document.createElement('span');
		errorMsg.className = 'favorite-city-card__error';
		errorMsg.textContent = 'Erro ao excluir cidade';
		cardElement.appendChild(errorMsg);
		setTimeout(() => errorMsg.remove(), 3000);
	}
};

const debounce = (fn, delay) => {
	let timer;
	return (...args) => {
		clearTimeout(timer);
		timer = setTimeout(() => fn(...args), delay);
	};
};

let weatherCache = {};

const loadWeatherForCity = async favorite => {
	const city = favorite?.city;
	if (!city?.cloc || !city?.cid) return false;

	try {
		const result = await ApiClient.post(
			appLocalUrl(`/json/myclimatempo/user/weatherNow?idlocale=${city.cloc}`),
		);

		if (!result.success) return false;

		const dataPath = result.data?.data?.getWeatherNow?.[0]?.data?.[0];
		if (!dataPath?.locale || !dataPath?.weather) return false;

		if (
			Number(dataPath.locale.idlocale) !== Number(city.cloc) ||
			Number(dataPath.locale.idcity) !== Number(city.cid)
		) {
			return false;
		}
		if (
			!dataPath.weather.icon ||
			dataPath.weather.temperature === undefined ||
			dataPath.weather.temperature === null
		) {
			return false;
		}

		weatherCache[city.cid] = {
			localeId: String(city.cloc),
			icon: dataPath.weather.icon,
			temperature: dataPath.weather.temperature,
		};
		return true;
	} catch {
		return false;
	}
};

const loadWeatherData = async (favoritesToLoad) => {
	const citiesToFetch = favoritesToLoad.filter(
		favorite =>
			favorite.city?.cloc &&
			favorite.city?.cid &&
			String(weatherCache[favorite.city.cid]?.localeId || '') !==
				String(favorite.city.cloc),
	);

	await Promise.allSettled(
		citiesToFetch.map(favorite => loadWeatherForCity(favorite)),
	);

	return favoritesToLoad.filter(favorite =>
		Boolean(
			weatherCache[favorite?.city?.cid] &&
			String(weatherCache[favorite.city.cid].localeId) ===
				String(favorite.city.cloc),
		),
	);
};

const getWeatherForCity = (cityId) => {
	return weatherCache[cityId] || null;
};

const enableHorizontalWheelScroll = (list) => {
	list.tabIndex = 0;
	list.setAttribute('role', 'region');
	list.setAttribute(
		'aria-label',
		'Cidades favoritas com rolagem horizontal',
	);

	list.addEventListener(
		'wheel',
		(event) => {
			if (event.ctrlKey) return;

			const maxScrollLeft = list.scrollWidth - list.clientWidth;
			if (maxScrollLeft <= 0) return;

			const rawDelta =
				Math.abs(event.deltaX) > Math.abs(event.deltaY)
					? event.deltaX
					: event.deltaY;
			if (!rawDelta) return;

			let delta = rawDelta;
			if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
				delta *= 16;
			} else if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
				delta *= list.clientWidth;
			}

			const nextScrollLeft = Math.max(
				0,
				Math.min(maxScrollLeft, list.scrollLeft + delta),
			);

			// Nas extremidades, mantém a rolagem vertical normal da página.
			if (nextScrollLeft === list.scrollLeft) return;

			event.preventDefault();
			list.scrollLeft = nextScrollLeft;
		},
		{ passive: false },
	);
};

const getForecastPage = () => {
	return document.getElementById('mainContent')?.dataset?.forecastPage || '';
};

const handleCitySelection = async city => {
	const forecastUrl = buildFavoriteCityForecastUrl(city, getForecastPage());
	if (forecastUrl) {
		window.location.assign(forecastUrl);
		return;
	}

	if (typeof citySelectionHandler === 'function') {
		await citySelectionHandler(city);
	}
};

const searchCities = async (query) => {
	const params = new URLSearchParams();
	params.append('name', query);

	const result = await ApiClient.post(appLocalUrl('/json/busca-por-nome'), params, {
		headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
	});

	if (!result.success) return [];

	const data = result.data;
	const cities = Array.isArray(data)
		? data.find(item => item.type?.toLowerCase() === 'city')
		: null;
	return cities?.response?.data?.slice(0, 5) || [];
};

const handleAddCity = () => {
	const container = document.getElementById(CONTAINER_ID);
	if (!container) return;

	const existing = container.querySelector('.favorite-cities-search');
	if (existing) {
		existing.remove();
		return;
	}

	const searchContainer = document.createElement('div');
	searchContainer.className = 'favorite-cities-search';

	const inputWrapper = document.createElement('div');
	inputWrapper.className = 'favorite-cities-search__input-wrapper';

	const iconWrapper = document.createElement('span');
	iconWrapper.className = 'favorite-cities-search__icon';
	const searchIcon = document.createElement('img');
	searchIcon.src = appAssetUrl('/dist/images/v2/svg/autocomplete-search.svg');
	searchIcon.alt = 'Buscar';
	iconWrapper.appendChild(searchIcon);

	const input = document.createElement('input');
	input.type = 'text';
	input.placeholder = 'buscar cidade';

	inputWrapper.appendChild(iconWrapper);
	inputWrapper.appendChild(input);

	const resultsList = document.createElement('ul');
	resultsList.className = 'favorite-cities-search__results';

	searchContainer.appendChild(inputWrapper);
	searchContainer.appendChild(resultsList);

	const header = container.querySelector('.favorite-cities-header');
	if (header && header.nextSibling) {
		container.insertBefore(searchContainer, header.nextSibling);
	} else {
		container.appendChild(searchContainer);
	}

	const onInput = debounce(async () => {
		const query = input.value.trim();
		resultsList.innerHTML = '';

		if (query.length < 3) return;

		try {
			const cities = await searchCities(query);
			cities.forEach(place => {
				const uf = LocalStorageHelper.resolveLocationCode(place);
				const li = document.createElement('li');
				li.textContent = `${place.city} - ${uf}`;
				li.addEventListener('click', async () => {
					const cityData = {
						cid: place.idcity,
						cloc: place.idlocale,
						cname: place.city,
						uf: place.uf,
						ac: place.ac,
						country: place.country,
						label: `${place.city} - ${uf}`,
						clat: place.latitude,
						clon: place.longitude,
					};

					isInternalUpdate = true;
					let actionResult;
					try {
						actionResult = await FavoriteCityService.addFavorite(cityData);
					} finally {
						isInternalUpdate = false;
					}
					if (actionResult.status === 'error') {
						const errorMsg = document.createElement('span');
						errorMsg.className = 'favorite-city-card__error';
						errorMsg.textContent = 'Erro ao adicionar cidade';
						li.appendChild(errorMsg);
						return;
					}
					searchContainer.remove();

					const result = await loadFavorites();
					if (result) {
						favorites = await loadWeatherData(result);
						if (result.length > 0 && favorites.length === 0) {
							removeContainer();
							return;
						}
						renderSection(favorites);
					}
				});
				resultsList.appendChild(li);
			});
		} catch {}
	}, 300);

	input.addEventListener('input', onInput);
	input.focus();
};

const handleCollapse = (cardElement) => {
	cardElement.classList.remove('favorite-city-card--expanded');
	const actionsContainer = cardElement.querySelector('.favorite-city-card__actions');
	if (actionsContainer) {
		actionsContainer.innerHTML = '';
	}
	expandedCardId = null;
};

const handleExpand = (cardElement) => {
	const currentExpanded = document.querySelector('.favorite-city-card--expanded');
	if (currentExpanded) {
		handleCollapse(currentExpanded);
	}

	cardElement.classList.add('favorite-city-card--expanded');
	expandedCardId = cardElement.dataset.cityId || null;

	const actionsContainer = cardElement.querySelector('.favorite-city-card__actions');
	if (actionsContainer) {
		const deleteBtn = document.createElement('button');
		deleteBtn.className = 'favorite-city-card__delete-btn';
		deleteBtn.textContent = 'Excluir cidade';
		deleteBtn.addEventListener('click', () => handleDelete(cardElement.dataset.cityId, cardElement));

		actionsContainer.appendChild(deleteBtn);
	}
};

const renderCard = (favorite) => {
	const card = document.createElement('div');
	card.className = 'favorite-city-card';
	card.dataset.cityId = favorite.city.cid;

	const content = document.createElement('div');
	content.className = 'favorite-city-card__content';
	const selectButton = document.createElement('button');
	selectButton.type = 'button';
	selectButton.className = 'favorite-city-card__select-btn';
	selectButton.setAttribute(
		'aria-label',
		getForecastPage()
			? `Ver previsão de ${favorite.city.label}`
			: `Selecionar ${favorite.city.label}`,
	);
	selectButton.addEventListener('click', () => {
		handleCitySelection(favorite.city);
	});

	// const drag = document.createElement('span');
	// drag.className = 'favorite-city-card__drag';
	// drag.textContent = '⁞⁞';

	const name = document.createElement('span');
	name.className = 'favorite-city-card__name';
	name.textContent = favorite.city.label;

	const weather = document.createElement('div');
	weather.className = 'favorite-city-card__weather';

	const weatherData = getWeatherForCity(favorite.city.cid);
	if (!weatherData) return null;

	const icon = document.createElement('img');
	icon.src = appAssetUrl(`/dist/images/v2/svg/${weatherData.icon}.svg`);
	icon.width = 28;
	icon.height = 28;
	icon.alt = 'Clima';

	const tempSpan = document.createElement('span');
	tempSpan.textContent = `${weatherData.temperature}°`;

	weather.appendChild(icon);
	weather.appendChild(tempSpan);

	const expandBtn = document.createElement('button');
	expandBtn.className = 'favorite-city-card__expand-btn';
	expandBtn.textContent = '⋮';
	expandBtn.addEventListener('click', () => {
		handleExpand(card);
	});

	const closeBtn = document.createElement('button');
	closeBtn.className = 'favorite-city-card__close-btn';
	closeBtn.textContent = '✕';
	closeBtn.addEventListener('click', () => {
		handleCollapse(card);
	});

	// content.appendChild(drag);
	selectButton.appendChild(name);
	selectButton.appendChild(weather);
	content.appendChild(selectButton);
	content.appendChild(expandBtn);
	content.appendChild(closeBtn);

	const actions = document.createElement('div');
	actions.className = 'favorite-city-card__actions';

	card.appendChild(content);
	card.appendChild(actions);

	return card;
};

const renderSection = (favoritesData) => {
	const container = document.getElementById(CONTAINER_ID);
	if (!container) return;

	container.innerHTML = '';
	container.setAttribute('aria-busy', 'false');

	const header = document.createElement('div');
	header.className = 'favorite-cities-header';

	const title = document.createElement('h2');
	title.className = 'favorite-cities-title';
	title.textContent = 'Minhas Cidades favoritas';

	const addBtn = document.createElement('button');
	addBtn.className = 'favorite-cities-add-btn';
	addBtn.addEventListener('click', handleAddCity);

	const addText = document.createTextNode('Adicionar Cidade');
	const addIcon = document.createElement('span');
	addIcon.className = 'favorite-cities-add-btn__icon';
	addIcon.textContent = '+';

	addBtn.appendChild(addText);
	addBtn.appendChild(addIcon);

	header.appendChild(title);
	header.appendChild(addBtn);
	container.appendChild(header);

	if (!favoritesData || favoritesData.length === 0) {
		const empty = document.createElement('div');
		empty.className = 'favorite-cities-empty';
		empty.innerHTML = `Nenhuma cidade adicionada. <a href="${appLocalUrl(`/meu-climatempo`)}">Gerenciar favoritos</a>`;
		container.appendChild(empty);
		return;
	}

	const list = document.createElement('div');
	list.className = 'favorite-cities-list';
	enableHorizontalWheelScroll(list);

	favoritesData.forEach((favorite) => {
		const card = renderCard(favorite);
		if (card) {
			list.appendChild(card);
		}
	});

	container.appendChild(list);
};

const init = async ({ onCitySelect = null } = {}) => {
	const container = document.getElementById(CONTAINER_ID);
	if (!container) return;
	citySelectionHandler = onCitySelect;

	await syncSubscriptionPlan();

	if (!isPremiumUser()) {
		removeContainer();
		return;
	}

	try {
		const result = await loadFavorites();

		if (!result) {
			removeContainer();
			return;
		}

		favorites = await loadWeatherData(result);
		if (result.length > 0 && favorites.length === 0) {
			removeContainer();
			return;
		}
		renderSection(favorites);
	} catch {
		removeContainer();
	}

	const refreshFavorites = () => {
		if (isInternalUpdate) return;

		const container = document.getElementById(CONTAINER_ID);
		if (!container) return;

		try {
			const updated = FavoriteCityService.getVisibleFavorites();
			loadWeatherData(updated).then(validFavorites => {
				if (updated.length > 0 && validFavorites.length === 0) {
					removeContainer();
					return;
				}

				favorites = validFavorites;
				renderSection(favorites);
			});
		} catch {}
	};
	document.addEventListener('favoriteChanged', refreshFavorites);
	document.addEventListener('favoriteSync', refreshFavorites);
};

const favoriteCitiesHomepage = {
	init,
};

export default favoriteCitiesHomepage;
